import { type QueryClient, queryOptions } from '@tanstack/react-query';
import type { CostHistoryResponse } from '../data/cost-history';
import type { Event } from '../data/event';
import type { ExceptionLog } from '../data/exception-log';
import type { OnboardingOverview } from '../data/onboarding';
import type { Order } from '../data/order';
import type { Product } from '../data/product';
import type { Receipt } from '../data/receipt';
import type { ReceivingSession, VendorInstructions } from '../data/receiving';
import type { Store } from '../data/store';
import type { Upload } from '../data/upload';
import type { UploaderConnection } from '../data/uploader-connection';
import type { Vendor, VendorIntegration } from '../data/vendor';
import { persistMeta } from './persisted-schemas';

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;

export const storesQueryOptions = () =>
  queryOptions<Store[]>({
    queryKey: ['stores'],
    meta: persistMeta('stores'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const productsQueryOptions = (storeId: string) =>
  queryOptions<Product[]>({
    queryKey: ['stores', storeId, 'products'],
    meta: persistMeta('products'),
    // Products change only on nightly uploads; route loaders + refetch-on-focus
    // cover freshness, so no background poll is needed.
    staleTime: 30 * MINUTES,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const vendorsQueryOptions = (storeId: string) =>
  queryOptions<Vendor[]>({
    queryKey: ['stores', storeId, 'vendors'],
    meta: persistMeta('vendors'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const vendorIntegrationsQueryOptions = (storeId: string) =>
  queryOptions<VendorIntegration[]>({
    queryKey: ['stores', storeId, 'vendor-integrations'],
    staleTime: 24 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const ordersQueryOptions = (storeId: string) =>
  queryOptions<Order[]>({
    queryKey: ['stores', storeId, 'orders'],
    meta: persistMeta('orders'),
    staleTime: 3 * HOURS,
    gcTime: 12 * HOURS,
    retry: 2,
  });

export const orderQueryOptions = (storeId: string, orderId: string) =>
  queryOptions<Order>({
    queryKey: ['stores', storeId, 'orders', orderId],
    meta: persistMeta('orders'),
    // The v2 order-edit page's authoritative read. Every v2 mutation writes its
    // response here, and order_updated pushes invalidate it (MR3). No
    // refetchInterval at steady state — MR3 adds one gated on a closed socket.
    staleTime: 30 * SECONDS,
  });

// Picks the more-recent of two Order objects for the same id. `version` (the
// server-owned optimistic-concurrency counter, Plan 3) is the only thing
// allowed to decide recency — arrival order over the network is not: the v2
// items and options edits carry independent FlushGuards
// (pages/orders/edit/hooks/use-order-items-v2.ts) with no guard between THEM,
// and the order query itself can refetch in the background, so a batchSetItems
// response, a setOrderOptions response, and a plain GET can all resolve out of
// order for the same order and must not blindly overwrite a newer one.
export function newerOrder(current: Order | undefined, incoming: Order): Order {
  return current && current.version > incoming.version ? current : incoming;
}

// Lands a fresh Order in both places one lives — the per-order cache and the
// store's orders list — through newerOrder, so no code path can forget the
// version gate. Every writer of these keys (mutation responses, 409-rebase
// recovery) must come through here rather than hand-rolling setQueryData:
// the gate only holds if it is impossible to opt out of.
export function writeOrderCache(
  queryClient: QueryClient,
  storeId: string,
  orderId: string,
  incoming: Order
): void {
  queryClient.setQueryData(orderQueryOptions(storeId, orderId).queryKey, (cached?: Order) =>
    newerOrder(cached, incoming)
  );
  queryClient.setQueryData(ordersQueryOptions(storeId).queryKey, (old: Order[] = []) =>
    old.map((o) => (o.id === orderId ? newerOrder(o, incoming) : o))
  );
}

export const uploadsQueryOptions = (storeId: string) =>
  queryOptions<Upload[]>({
    queryKey: ['stores', storeId, 'uploads'],
    meta: persistMeta('uploads'),
    // Active monitoring is the gated 5s poll in useUploads; this only governs
    // refetch-on-focus, so a short window is unnecessary churn.
    staleTime: 2 * MINUTES,
    gcTime: 1 * HOURS,
    retry: 2,
  });

export const companionConnectionsQueryOptions = (storeId: string) =>
  queryOptions<UploaderConnection[]>({
    queryKey: ['stores', storeId, 'companions', 'connections'],
    // WebSocket (companion_status_changed) delivers real-time updates, so the
    // focus-refetch window can be generous.
    staleTime: 2 * MINUTES,
    gcTime: 1 * HOURS,
    retry: 2,
  });

export const exceptionLogsQueryOptions = (storeId: string) =>
  queryOptions<ExceptionLog[]>({
    queryKey: ['stores', storeId, 'exception-logs'],
    meta: persistMeta('exception-logs'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
    retry: 2,
  });

export const receiptsQueryOptions = (storeId: string) =>
  queryOptions<Receipt[]>({
    queryKey: ['stores', storeId, 'receipts'],
    meta: persistMeta('receipts'),
    staleTime: 12 * HOURS,
    gcTime: 24 * HOURS,
  });

export const receivingSessionsQueryOptions = (storeId: string) =>
  queryOptions<ReceivingSession[]>({
    queryKey: ['stores', storeId, 'receiving-sessions'],
    // WebSocket (receiving_session_updated) delivers real-time updates, so the
    // focus-refetch window can be generous.
    staleTime: 2 * MINUTES,
    gcTime: 1 * HOURS,
    retry: 2,
    // The WS subscription that keeps this list live only runs while the list
    // page is mounted. While the user is deep in a session the list is
    // unmounted and misses every update — including the terminal delete on
    // cancel/complete/abandon. So always re-validate on (re)entry, otherwise a
    // just-cancelled session lingers as a stale row until staleTime elapses.
    refetchOnMount: 'always',
  });

export const vendorInstructionsQueryOptions = (storeId: string, vendorNormalizedName: string) =>
  queryOptions<VendorInstructions>({
    queryKey: ['stores', storeId, 'receiving', 'instructions', vendorNormalizedName],
    staleTime: 24 * HOURS,
    gcTime: 24 * HOURS,
    retry: false,
  });

export const productCostHistoryQueryOptions = (
  storeId: string,
  vendorId: string,
  productId: string
) =>
  queryOptions<CostHistoryResponse>({
    queryKey: ['stores', storeId, 'vendors', vendorId, 'products', productId, 'cost-history'],
    staleTime: 30 * MINUTES,
    gcTime: 30 * MINUTES,
    retry: 1,
  });

export const onboardingOverviewQueryOptions = () =>
  queryOptions<OnboardingOverview>({
    queryKey: ['admin', 'onboarding', 'overview'],
    staleTime: 5 * MINUTES,
    gcTime: 10 * MINUTES,
    retry: 2,
  });

export const onboardingEventsQueryOptions = (storeId: string) =>
  queryOptions<Event[]>({
    queryKey: ['admin', 'onboarding', 'events', storeId],
    staleTime: 5 * MINUTES,
    gcTime: 30 * MINUTES,
    retry: 2,
  });
